<template>
  <div class="background h-full">
    <product-studio
      v-if="ready"
      ref="studio"
      context="frontend"
      :default-options="defaultOptions"
      :is-approved="
        $store.state?.store?.active?.approval?.status === 'approved'
      "
      :design-url="designUrl"
      :config="
        !!$store.state.subdomain.enableBulkStudio
          ? bulkPurchaseConfig
          : standardConfig
      "
      @editor-updated="onEditorUpdated"
      @after-canvas-initialised="afterCanvasInitialised"
    />
    <add-to-cart-popup v-if="includeAddToCart" hide-returns-policy />
  </div>
</template>

<script>
import {formatUrl, matchDomains} from '@teemill/common/helpers';
import {AddToCartPopup} from '@teemill/modules/product';
import {computed} from 'vue';
import {useHead} from '@vueuse/head';
import {useGtm} from '@teemill/common/services';
import {ProductStudio} from '@teemill/modules/studio';
import {useLiveChat, chatSupportImage} from '@teemill/modules/live-chat';
import {standardConfig, bulkPurchaseConfig} from './configs';

import {ConfirmLeaveMixin} from '@teemill/components/mixins';
import {events} from '@teemill/common/services';

export default {
  name: 'StudioPage',

  components: {
    ProductStudio,
    AddToCartPopup,
  },

  mixins: [ConfirmLeaveMixin],

  provide() {
    return {
      getProductUrlName: this.getProductUrlName,
      getPriceDifference: this.getPriceDifference,
      getPreCustomisation: this.getPreCustomisation,
    };
  },

  beforeRouteLeave(to, from, next) {
    this.onBeforeRouteLeave(to, from, next);
  },

  // mixins: [page],
  data() {
    return {
      ready: true,
      containerClass: 'container-fluid',
      showFooter: false,
      limit: 0,
      productUrlName: false,
      singlePageApp: true,
      pageTitle: 'Design a product',
      defaultOptions: {},
      designUrl: undefined,
      skuMap: {
        'mens-custom-printed-sweaters': {
          sku: 'RNA4',
          colour: 'White',
        },
        'mens-custom-printed-hoodies': {
          sku: 'RNA7',
          colour: 'White',
        },
        'mens-custom-printed-baseball-tees': {
          sku: 'RNA8',
          colour: 'Black-White',
        },
        'mens-custom-printed-short-sleeve-baseball-tees': {
          sku: 'RNA25',
          colour: 'Black-White',
        },
        'mens-custom-printed-circular-economy-t-shirts': {
          sku: 'REA1',
          colour: 'Grey',
        },
        'womens-custom-printed-t-shirts': {
          sku: 'RNB14',
          colour: 'White',
        },
        'womens-custom-printed-circular-economy-t-shirts': {
          sku: 'REB14',
          colour: 'Grey',
        },
        'womens-custom-printed-hoodies': {
          sku: 'RNB13',
          colour: 'Light Heather',
        },
        'womens-custom-printed-vest-top': {
          sku: 'RNB3',
          colour: 'White',
        },
        'womens-custom-printed-t-shirt-dress': {
          sku: 'RNB24',
          colour: 'Athletic Grey',
        },
        'womens-custom-printed-sweaters': {
          sku: 'RNB18',
          colour: 'Light Heather',
        },
        'womens-custom-printed-baseball-tees': {
          sku: 'RNB15',
          colour: 'Black-White',
        },
        'custom-printed-tote-bags': {
          sku: 'RNT1',
          colour: 'White',
        },
        'kids-custom-printed-t-shirts': {
          sku: 'RNC1',
          colour: 'White',
        },
        'custom-stickers': {
          sku: 'RNST1',
          colour: 'White',
        },
        'custom-art-prints': {
          sku: 'RNP5',
          colour: 'White',
        },
        'custom-landscape-art-prints': {
          sku: 'RNP6',
          colour: 'White',
        },
        default: null,
      },

      includeAddToCart: false,

      standardConfig,
      bulkPurchaseConfig,
      template: null,
    };
  },

  created() {
    this.setMetaData();

    const path = this.$route.path.replace(/\//g, '');
    if (this.skuMap[path]) {
      this.defaultOptions = this.skuMap[path];
    } else if (this.$route.query.product) {
      this.defaultOptions = {
        sku: this.$route.query.product,
        colour: this.$route.query.colour,
      };

      if (this.$route.query.design) {
        const designUrl = formatUrl(this.$route.query.design);

        // only allow designs from Image API
        if (matchDomains(designUrl, ['teemill.com', 'localhost', 'podos.io'])) {
          this.designUrl = designUrl;
        }
      }
    } else {
      this.defaultOptions = this.skuMap.default;
    }

    this.getCustomiseItDesign();
  },

  beforeMount() {
    if (this.$route.params.productUrlName) {
      this.productUrlName = this.$route.params.productUrlName;
    }
  },

  mounted() {
    history.replaceState(history.state, 'Studio', this.$route.path);

    // the add to cart popup isn't required on initial page load
    // so to prioritise loading of Studio over the add to cart popup, and to
    // eliminate the need for more packages, the component isn't rendered for 3 seconds
    setTimeout(() => {
      this.includeAddToCart = true;
    }, 3000);

    this.sendViewItemEvent();

    if (this.isRapa()) {
      const {updateGroup, notify} = useLiveChat();

      updateGroup('studio');

      setTimeout(() => {
        notify({
          title: 'Live Support',
          body: 'A member of our team is online and available to assist.',
          image: chatSupportImage,
          buttonStyle: 'default',
          buttonText: 'Start Chatting',
        });
      }, 6000);
    }
  },

  methods: {
    isRapa() {
      return (
        this.$store.state.subdomain &&
        this.$store.state.subdomain.company.id === 14
      );
    },

    isMobile() {
      return window.innerWidth < 992;
    },

    getProductUrlName() {
      return false;
    },

    getPriceDifference() {
      return this.priceDifference;
    },

    getPreCustomisation() {
      return this.preCustomisation;
    },

    getCustomiseItDesign() {
      const customiseItUrl = this.$route.params.customiseItUrl;
      if (customiseItUrl) {
        this.ready = false;

        this.axios
          .post(formatUrl('/omnis/v3/dashboard/studio/getCustomiseItDesign/'), {
            customiseItUrl,
          })
          .then(({data}) => {
            if (data && data.success) {
              this.defaultOptions = {
                sku: data.base_sku,
                colour: this.$route.query.colour ?? data.colour,
              };

              this.priceDifference = data.price_difference;
              this.preCustomisation = data.pre_customisation;

              let designsToWaitFor = 0;

              const onImageLoaded = () => {
                designsToWaitFor -= 1;
                if (designsToWaitFor === 0) {
                  this.ready = true;
                }
              };

              if (data.template) {
                this.template = data.template;

                this.ready = true;
              } else {
                if (data.design_file_url) {
                  designsToWaitFor += 1;
                  const img = new Image();
                  img.onload = onImageLoaded;
                  img.crossOrigin = 'Anonymous';
                  img.src = formatUrl(data.design_file_url);
                  this.defaultOptions.designFile = img;
                }

                if (data.back_design_file_url) {
                  designsToWaitFor += 1;
                  const img = new Image();
                  img.onload = onImageLoaded;
                  img.crossOrigin = 'Anonymous';
                  img.src = formatUrl(data.back_design_file_url);
                  this.defaultOptions.backDesignFile = img;
                }

                if (designsToWaitFor === 0) {
                  this.ready = true;
                }
              }
            } else {
              this.ready = true;
            }
          });
      }
    },

    setMetaData() {
      useHead(
        computed(() => {
          const host = `${location.protocol}//${location.hostname}`;

          return {
            title: `Design your own T-Shirt | ${this.$store.state.title}`,
            link: [{rel: 'canonical', href: `${host}/studio/`}],
          };
        })
      );
    },

    sendViewItemEvent() {
      useGtm().viewItem({
        contentType: 'product',
        items: [
          {
            name: 'Custom Product',
            brand: JSON.stringify(this.$store.state.subdomain.company.name),
          },
        ],
        currency: this.$store?.state?.subdomain?.currency ?? 'GBP',
        userId: this.$store.state.subdomain.sessionId,
      });
    },

    onEditorUpdated(e) {
      this.hasUnsavedChanges = e.hasUnsavedChanges;
    },

    afterCanvasInitialised(canvas) {
      if (this.template) {
        events.once('studio.frontBoundsSet', () => {
          canvas.fromTemplate(this.template);
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.background {
  background-color: white;
}
</style>
